import { useState, useEffect } from 'react';
import { Stack, Button as MuiButton, Typography, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

// --- Styled Pagination Buttons ---
const PaginationButton = styled(MuiButton)(({ theme }) => ({
    minWidth: '1.75rem',
    width: '1.75rem',
    height: '1.75rem',
    padding: '0',
    fontSize: '0.7rem',
    fontWeight: 600,
    lineHeight: 1,
    color: '#0d6efd',
    backgroundColor: '#e2f0ff',
    border: '0.0625rem solid #ccc',
    borderRadius: '0.25rem',
    textTransform: 'none',
    boxShadow: 'none',

    '&:hover': {
        backgroundColor: '#bde2ff',
        boxShadow: 'none',
    },

    '&.Mui-disabled': {
        color: '#6c757d',
        backgroundColor: '#e2f0ff',
        pointerEvents: 'none',
    }
}));

// --- Custom Pagination Component ---
const CustomPagination = ({ count, page, onChange }) => {
    const [inputPage, setInputPage] = useState(page + 1);

    // Sync input with external page changes
    useEffect(() => {
        setInputPage(page + 1);
    }, [page]);

    const handleFirstPage = () => onChange(null, 1);
    const handlePreviousPage = () => onChange(null, page); // `page` is 0-based, so `page` is previous
    const handleNextPage = () => onChange(null, page + 2);
    const handleLastPage = () => onChange(null, count);

    const handleInputChange = (e) => {
        setInputPage(e.target.value);
    };

    const handleGoToPage = () => {
        const pageNum = parseInt(inputPage, 10);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= count) {
            onChange(null, pageNum);
        } else {
            setInputPage(page + 1); // Reset if invalid
        }
    };

    const handleInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleGoToPage();
        }
    };

    const isFirstPage = page === 0;
    const isLastPage = page === count - 1;

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            className="pagination"
            sx={{
                width: "100%",
                justifyContent: "flex-end",
                background: 'transparent',
                color: '#424242',
                fontSize: '0.75rem'
            }}
        >
            {/* First Page Button */}
            <span title="First Page">
                <PaginationButton onClick={handleFirstPage} disabled={isFirstPage}>«</PaginationButton>
            </span>

            {/* Previous Page Button */}
            <span title="Previous Page">
                <PaginationButton onClick={handlePreviousPage} disabled={isFirstPage}>&lt;</PaginationButton>
            </span>

            {/* Page Indicator */}
            <Typography
                component="span"
                className="page-number font-12"
                title={`${page + 1} of ${count}`}
                sx={{
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    fontWeight: '600',
                    padding: '0.375rem 0',
                    margin: '0.3125rem 0.625rem',
                    color: '#6c757d',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <span title={`${page + 1} of ${count}`}>{page + 1} of {count}</span>
            </Typography>

            {/* Next Page Button */}
            <span title="Next Page">
                <PaginationButton onClick={handleNextPage} disabled={isLastPage}>&gt;</PaginationButton>
            </span>

            {/* Last Page Button */}
            <span title="Last Page">
                <PaginationButton onClick={handleLastPage} disabled={isLastPage}>»</PaginationButton>
            </span>

            {/* Page Input */}
            <TextField
                // className="font-12 pagination-input"
                type="number"
                value={inputPage}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyPress}
                onBlur={handleGoToPage}
                size="small"
                variant="outlined"
                placeholder="Page"
                sx={{
                    width: '3.25rem',  // Adjust width for a better fit
                    height: '1.75rem',  // Increased height for a better user experience
                    fontSize: '0.75rem',
                    fontWeight: "500",
                    backgroundColor: '#FFFFFF',
                    marginLeft: '0.625rem',
                    '& .MuiOutlinedInput-root': {
                        fontWeight: '400',
                        height: '1.75rem',
                        fontSize: '0.75rem',
                        border: '0.0625rem  #ccc',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7AD5D2', // Focus border color
                        },
                    },
                    '& .MuiInputBase-input': {
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textAlign: 'center',
                        padding: '0.1875rem 0rem'
                    }
                }}
                inputProps={{ min: 1, max: count, 'aria-label': 'page number input' }}
            />

            {/* Go Button with Sky Blue Border */}
            <span title="Go">
                <MuiButton
                    variant="contained"
                    color="primary"
                    sx={{
                        minWidth: '1.75rem',
                        width: '1.75rem',
                        height: '1.75rem',
                        fontSize: '0.7rem',
                        backgroundColor: '#7AD5D2',
                        color: '#FFFFFF',
                        padding: '0',
                        border: 'none',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#64c2c1',
                            boxShadow: 'none'
                        }
                    }}
                    onClick={handleGoToPage}
                >
                    Go
                </MuiButton>
            </span>
        </Stack>
    );
};

export default CustomPagination;
